import React, { useState, useEffect } from 'react';
import './StudyGroups.css';

function StudyGroups() {
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupMessages, setGroupMessages] = useState([]);
  const [groupMembers, setGroupMembers] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [allPMs, setAllPMs] = useState([]);
  const [filterTab, setFilterTab] = useState('all'); // all, my, invited
  const [loading, setLoading] = useState(false);
  
  // Create Group Form
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDescription, setNewGroupDescription] = useState('');
  const [newGroupTrack, setNewGroupTrack] = useState('CAPM');
  
  // Invite PM
  const [selectedPmForInvite, setSelectedPmForInvite] = useState('');
  
  // New Message
  const [newMessage, setNewMessage] = useState('');
  
  useEffect(() => {
    fetchGroups();
  }, []);
  
  const fetchGroups = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_URL}/learning/groups`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!res.ok) throw new Error('Failed to fetch groups');
      
      const data = await res.json();
      setGroups(data.groups || []);
    } catch (err) {
      console.error('Error fetching groups:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchGroupDetails = async (groupId) => {
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_URL}/learning/groups/${groupId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!res.ok) throw new Error('Failed to fetch group details');
      
      const data = await res.json();
      setGroupMembers(data.group.members || []);
      setGroupMessages(data.group.messages || []);
    } catch (err) {
      console.error('Error fetching group details:', err);
    }
  };
  
  const handleCreateGroup = async (e) => {
    e.preventDefault();
    if (!newGroupName.trim()) return;
    
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_URL}/learning/groups`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: newGroupName,
          description: newGroupDescription,
          certificationTrack: newGroupTrack
        })
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to create group');
      }
      
      const data = await res.json();
      alert('Study group created successfully!');
      setShowCreateModal(false);
      setNewGroupName('');
      setNewGroupDescription('');
      setNewGroupTrack('CAPM');
      fetchGroups();
      setSelectedGroup(data.group);
      fetchGroupDetails(data.group.id);
    } catch (err) {
      console.error('Error creating group:', err);
      alert(err.message || 'Failed to create group');
    }
  };
  
  const handleSelectGroup = (group) => {
    setSelectedGroup(group);
    fetchGroupDetails(group.id);
  };
  
  const handleInvitePM = async () => {
    if (!selectedPmForInvite || !selectedGroup) return;
    
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_URL}/learning/groups/${selectedGroup.id}/members`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          pmId: parseInt(selectedPmForInvite)
        })
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to invite PM');
      }
      
      alert('Invitation sent!');
      setShowInviteModal(false);
      setSelectedPmForInvite('');
      fetchGroupDetails(selectedGroup.id);
    } catch (err) {
      console.error('Error inviting PM:', err);
      alert(err.message || 'Failed to send invitation');
    }
  };
  
  const handleRespondToInvite = async (groupId, accept) => {
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_URL}/learning/groups/${groupId}/respond`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          accept: accept
        })
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to respond to invitation');
      }
      
      alert(accept ? 'You joined the group!' : 'Invitation declined');
      fetchGroups();
      if (selectedGroup && selectedGroup.id === groupId) {
        if (accept) {
          fetchGroupDetails(groupId);
        } else {
          setSelectedGroup(null);
        }
      }
    } catch (err) {
      console.error('Error responding to invitation:', err);
      alert(err.message || 'Failed to respond to invitation');
    }
  };
  
  const handleLeaveGroup = async (groupId) => {
    if (!window.confirm('Are you sure you want to leave this group?')) return;
    
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_URL}/learning/groups/${groupId}/members/me`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to leave group');
      }
      
      alert('You left the group');
      fetchGroups();
      if (selectedGroup && selectedGroup.id === groupId) {
        setSelectedGroup(null);
      }
    } catch (err) {
      console.error('Error leaving group:', err);
      alert(err.message || 'Failed to leave group');
    }
  };
  
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedGroup) return;
    
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_URL}/learning/groups/${selectedGroup.id}/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          messageText: newMessage
        })
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to send message');
      }
      
      setNewMessage('');
      fetchGroupDetails(selectedGroup.id);
    } catch (err) {
      console.error('Error sending message:', err);
      alert(err.message || 'Failed to send message');
    }
  };
  
  const fetchAllPMs = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_URL}/learning/pms/all`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!res.ok) throw new Error('Failed to fetch PMs');
      
      const data = await res.json();
      setAllPMs(data.pms || []);
    } catch (err) {
      console.error('Error fetching PMs:', err);
    }
  };
  
  const filteredGroups = groups.filter(group => {
    if (filterTab === 'all') return true;
    if (filterTab === 'my') return group.my_status === 'active';
    if (filterTab === 'invited') return group.my_status === 'invited';
    return true;
  });
  
  return (
    <div className="study-groups-container">
      <div className="groups-header">
        <h1>👥 Study Groups</h1>
        <button 
          onClick={() => setShowCreateModal(true)}
          className="create-group-btn"
        >
          ➕ Create Group
        </button>
      </div>
      
      <div className="groups-tabs">
        <button 
          className={`tab-btn ${filterTab === 'all' ? 'active' : ''}`}
          onClick={() => setFilterTab('all')}
        >
          All Groups ({groups.length})
        </button>
        <button 
          className={`tab-btn ${filterTab === 'my' ? 'active' : ''}`}
          onClick={() => setFilterTab('my')}
        >
          My Groups ({groups.filter(g => g.my_status === 'active').length})
        </button>
        <button 
          className={`tab-btn ${filterTab === 'invited' ? 'active' : ''}`}
          onClick={() => setFilterTab('invited')}
        >
          Invitations ({groups.filter(g => g.my_status === 'invited').length})
        </button>
      </div>
      
      <div className="groups-content">
        <div className="groups-list">
          {loading ? (
            <div className="loading">Loading groups...</div>
          ) : filteredGroups.length > 0 ? (
            filteredGroups.map(group => (
              <div 
                key={group.id} 
                className={`group-card ${selectedGroup?.id === group.id ? 'selected' : ''}`}
                onClick={() => handleSelectGroup(group)}
              >
                <div className="group-card-header">
                  <h3>{group.name}</h3>
                  <span className={`track-badge ${group.certification_track.toLowerCase()}`}>
                    {group.certification_track}
                  </span>
                </div>
                <p className="group-description">{group.description || 'No description'}</p>
                <div className="group-card-footer">
                  <span className="member-count">👤 {group.member_count} members</span>
                  {group.my_status === 'invited' && (
                    <span className="invited-badge">📬 Invited</span>
                  )}
                  {group.my_role === 'admin' && (
                    <span className="admin-badge">⚙️ Admin</span>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="no-groups">
              {filterTab === 'all' && 'No study groups yet. Create one to get started!'}
              {filterTab === 'my' && 'You haven\'t joined any groups yet.'}
              {filterTab === 'invited' && 'No pending invitations.'}
            </div>
          )}
        </div>
        
        {selectedGroup && (
          <div className="group-details">
            <div className="group-details-header">
              <div>
                <h2>{selectedGroup.name}</h2>
                <p className="group-meta">{selectedGroup.description}</p>
              </div>
              <div className="group-actions">
                {selectedGroup.my_status === 'invited' ? (
                  <>
                    <button 
                      onClick={() => handleRespondToInvite(selectedGroup.id, true)}
                      className="accept-btn"
                    >
                      ✓ Accept
                    </button>
                    <button 
                      onClick={() => handleRespondToInvite(selectedGroup.id, false)}
                      className="decline-btn"
                    >
                      ✖ Decline
                    </button>
                  </>
                ) : selectedGroup.my_status === 'active' ? (
                  <>
                    {selectedGroup.my_role === 'admin' && (
                      <button 
                        onClick={() => {
                          setShowInviteModal(true);
                          if (allPMs.length === 0) fetchAllPMs();
                        }}
                        className="invite-btn"
                      >
                        ➕ Invite PM
                      </button>
                    )}
                    <button 
                      onClick={() => handleLeaveGroup(selectedGroup.id)}
                      className="leave-btn"
                    >
                      🚪 Leave Group
                    </button>
                  </>
                ) : null}
              </div>
            </div>
            
            <div className="group-tabs-section">
              <div className="tab-section active">
                <h3>💬 Messages ({groupMessages.length})</h3>
                <div className="messages-container">
                  <div className="messages-list">
                    {groupMessages.length > 0 ? (
                      groupMessages.map(msg => (
                        <div key={msg.id} className="message-card">
                          <div className="message-header">
                            <strong>{msg.sender_name}</strong>
                            <span className="message-date">
                              {new Date(msg.created_at + 'Z').toLocaleString('en-US', { timeZone: 'America/New_York' })}
                            </span>
                          </div>
                          <div className="message-text">{msg.message_text}</div>
                        </div>
                      ))
                    ) : (
                      <div className="no-messages">No messages yet. Start the conversation!</div>
                    )}
                  </div>
                  {selectedGroup.my_status === 'active' && (
                    <form onSubmit={handleSendMessage} className="message-form">
                      <textarea
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Type your message..."
                        rows="3"
                        className="message-input"
                      />
                      <button 
                        type="submit" 
                        disabled={!newMessage.trim()}
                        className="send-message-btn"
                      >
                        📤 Send
                      </button>
                    </form>
                  )}
                </div>
              </div>
              
              <div className="tab-section">
                <h3>👥 Members ({groupMembers.length})</h3>
                <div className="members-list">
                  {groupMembers.map(member => (
                    <div key={member.id} className="member-card">
                      <div className="member-info">
                        <strong>{member.pm_name}</strong>
                        {member.role === 'admin' && <span className="role-badge">⚙️ Admin</span>}
                        {member.status === 'invited' && <span className="status-badge">📬 Invited</span>}
                      </div>
                      <span className="member-date">
                        Joined {new Date(member.joined_at).toLocaleDateString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Create Group Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Create Study Group</h3>
            <button 
              className="modal-close" 
              onClick={() => setShowCreateModal(false)}
            >
              ✖
            </button>
            
            <form onSubmit={handleCreateGroup} className="create-group-form">
              <div className="form-group">
                <label>Group Name *</label>
                <input
                  type="text"
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  placeholder="e.g., CAPM Study Group - Winter 2024"
                  required
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={newGroupDescription}
                  onChange={(e) => setNewGroupDescription(e.target.value)}
                  placeholder="What is this group for?"
                  rows="3"
                  className="form-textarea"
                />
              </div>
              
              <div className="form-group">
                <label>Certification Track *</label>
                <select
                  value={newGroupTrack}
                  onChange={(e) => setNewGroupTrack(e.target.value)}
                  className="form-select"
                >
                  <option value="CAPM">CAPM</option>
                  <option value="PMP">PMP</option>
                  <option value="Both">Both</option>
                </select>
              </div>
              
              <button 
                type="submit"
                disabled={!newGroupName.trim()}
                className="submit-btn"
              >
                Create Group
              </button>
            </form>
          </div>
        </div>
      )}
      
      {/* Invite PM Modal */}
      {showInviteModal && (
        <div className="modal-overlay" onClick={() => setShowInviteModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Invite PM to Group</h3>
            <button 
              className="modal-close" 
              onClick={() => setShowInviteModal(false)}
            >
              ✖
            </button>
            
            <div className="modal-body">
              <div className="form-group">
                <label>Select PM:</label>
                <select
                  value={selectedPmForInvite}
                  onChange={(e) => setSelectedPmForInvite(e.target.value)}
                  className="form-select"
                >
                  <option value="">-- Select PM --</option>
                  {allPMs.map(pm => (
                    <option key={pm.id} value={pm.id}>
                      {pm.full_name} ({pm.email})
                    </option>
                  ))}
                </select>
              </div>
              
              <button 
                onClick={handleInvitePM}
                disabled={!selectedPmForInvite}
                className="submit-btn"
              >
                Send Invitation
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default StudyGroups;
